/**
 * NFL Daily Reveal ("Gridiron Kings") Configuration
 *
 * Single source of truth for the NFL *prop-based* Daily Reveal mode. This config
 * is activated only when a Daily Reveal contest has `sport = 'NFL'` AND its
 * resolved mode is `prop_based` (explicit contest override — see `daily-reveal.ts`).
 *
 * NFL's sport-level `defaultMode` remains `stat_hit` so existing / `sport_default`
 * contests keep legacy TD-hit gameplay. New Gridiron Kings contests must set
 * `daily_reveal_mode = 'prop_based'` explicitly.
 *
 * IMPORTANT: This file describes *rules and defaults only*. It must not contain
 * any Sportsbook API / odds-feed client logic. Prop markets are sourced through
 * the provider architecture (manual / mock / csv_import / sportsbook_feed) and
 * frozen into the `daily_reveal_prop_markets` table. The rest of the game only
 * ever consumes those frozen internal records.
 */

/** Comparison operator used to grade a frozen prop against the official stat. */
export type NFLPropOperator = 'over' | 'over_or_equal' | 'over_or_true';

/** Which set of NFL fixtures a day's cards are drawn from. */
export type NFLGameWindow = 'TNF' | 'SUNDAY' | 'MNF' | 'CUSTOM';

/**
 * When a day locks for reveals/picks.
 * - `end_of_day` — locks at `closeTimeEt` (default 23:59 ET) that calendar day.
 * - `game_window_kickoff` — locks at the configured game window's kickoff.
 * - `first_relevant_game_kickoff` — locks at the earliest kickoff among the
 *   day's eligible markets.
 */
export type NFLDayCloseRule =
  | 'end_of_day'
  | 'game_window_kickoff'
  | 'first_relevant_game_kickoff';

/** How exceptional outcomes (injury/inactive/void/postponed) are graded. */
export type NFLPropExceptionalOutcomePolicy =
  | 'loss'
  | 'void'
  | 'credit_win'
  | 'admin_review';

/** Days of the NFL Daily Reveal week (Tuesday → Monday). */
export type NFLWeekDay =
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'
  | 'MONDAY';

/** Configuration for a single day/category within the NFL week. */
export interface NFLDailyRevealDayConfig {
  /** 1-based day number within the contest (1 = Tuesday … 7 = Monday). */
  dayNumber: number;
  dayOfWeek: NFLWeekDay;
  /** Stable machine key, e.g. `ground_king`. */
  categoryKey: string;
  /** Display name, e.g. `Ground King`. */
  categoryName: string;
  /** Internal prop stat type, e.g. `rushing_yards`. */
  propStatType: string;
  /** Grading operator for this category's props. */
  operator: NFLPropOperator;
  /** Human-friendly default threshold label used before markets are frozen. */
  defaultThresholdLabel: string;
  gameWindow: NFLGameWindow;
  /** Reveal window open time in ET (24h `HH:mm`). */
  openTimeEt: string;
  closeRule: NFLDayCloseRule;
  /** Only meaningful when `closeRule === 'end_of_day'`. */
  closeTimeEt?: string;
}

/** Top-level NFL Daily Reveal ruleset. */
export interface NFLDailyRevealConfig {
  sport: 'NFL';
  mode: 'prop_based';
  packSize: number;
  weekStartsOn: NFLWeekDay;
  timezone: string;
  /** Whether users may change their pick until the relevant lock deadline. */
  allowPickChangeBeforeLock: boolean;
  /** Whether operators may hand-author prop markets when no feed is available. */
  fallbackManualPropsEnabled: boolean;
  /** Default policy for players ruled out / games disrupted after lock. */
  defaultInjuryPolicy: NFLPropExceptionalOutcomePolicy | 'admin_controlled';
  days: NFLDailyRevealDayConfig[];
}

export const NFL_DAILY_REVEAL_CONFIG: NFLDailyRevealConfig = {
  sport: 'NFL',
  mode: 'prop_based',
  packSize: 3,
  weekStartsOn: 'TUESDAY',
  timezone: 'America/New_York',
  allowPickChangeBeforeLock: true,
  fallbackManualPropsEnabled: true,
  defaultInjuryPolicy: 'admin_controlled',
  days: [
    {
      dayNumber: 1,
      dayOfWeek: 'TUESDAY',
      categoryKey: 'ground_king',
      categoryName: 'Ground King',
      propStatType: 'rushing_yards',
      operator: 'over',
      defaultThresholdLabel: '50+ rushing yards',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 2,
      dayOfWeek: 'WEDNESDAY',
      categoryKey: 'passing_king',
      categoryName: 'Passing King',
      propStatType: 'passing_yards',
      operator: 'over',
      defaultThresholdLabel: '250+ passing yards',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 3,
      dayOfWeek: 'THURSDAY',
      categoryKey: 'touchdown_king',
      categoryName: 'Touchdown King',
      propStatType: 'anytime_touchdown',
      operator: 'over_or_true',
      defaultThresholdLabel: 'Score a TD',
      gameWindow: 'TNF',
      openTimeEt: '00:00',
      closeRule: 'game_window_kickoff',
    },
    {
      dayNumber: 4,
      dayOfWeek: 'FRIDAY',
      categoryKey: 'hands_king',
      categoryName: 'Hands King',
      propStatType: 'receiving_yards',
      operator: 'over',
      defaultThresholdLabel: '75+ receiving yards',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 5,
      dayOfWeek: 'SATURDAY',
      categoryKey: 'sack_king',
      categoryName: 'Sack King',
      propStatType: 'sacks',
      operator: 'over_or_equal',
      defaultThresholdLabel: '1+ sacks',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'end_of_day',
      closeTimeEt: '23:59',
    },
    {
      dayNumber: 6,
      dayOfWeek: 'SUNDAY',
      categoryKey: 'shootout_king',
      categoryName: 'Shootout King',
      propStatType: 'passing_touchdowns',
      operator: 'over_or_equal',
      defaultThresholdLabel: '2+ passing TDs',
      gameWindow: 'SUNDAY',
      openTimeEt: '00:00',
      closeRule: 'first_relevant_game_kickoff',
    },
    {
      dayNumber: 7,
      dayOfWeek: 'MONDAY',
      categoryKey: 'crown_king',
      categoryName: 'Crown King',
      propStatType: 'all_purpose_yards',
      operator: 'over',
      defaultThresholdLabel: '50+ all-purpose yards',
      gameWindow: 'MNF',
      openTimeEt: '00:00',
      closeRule: 'game_window_kickoff',
    },
  ],
};

/** Return the NFL Daily Reveal config (only NFL is prop-based today). */
export function getNflDailyRevealConfig(
  sport: string | null | undefined
): NFLDailyRevealConfig | null {
  return (sport ?? '').toUpperCase() === 'NFL' ? NFL_DAILY_REVEAL_CONFIG : null;
}

/** NFL weeks and lock windows are defined in US Eastern Time (same as DK Sportsbook). */
export const NFL_TIMEZONE = 'America/New_York';

/**
 * Format an instant as `YYYY-MM-DD` in America/New_York (matches DK app game dates).
 */
export function toNflEtDateString(instant: Date): string {
  return instant.toLocaleDateString('en-CA', { timeZone: NFL_TIMEZONE });
}

/**
 * Human-readable label for which NFL games a reveal day draws props from.
 */
export function describeNflGameWindow(gameWindow: NFLGameWindow): string {
  switch (gameWindow) {
    case 'TNF':
      return 'Thursday Night Football';
    case 'SUNDAY':
      return 'Sunday slate';
    case 'MNF':
      return 'Monday Night Football';
    default:
      return 'Custom window';
  }
}

/**
 * UTC bounds for an entire calendar day in America/New_York (for API date filters).
 * Handles EDT/EST without extra dependencies.
 */
export function getNflEtDayUtcBounds(dateEt: string): { startsAt: Date; endsAt: Date } {
  const [y, m, d] = dateEt.split('-').map(Number);
  // Seed with EDT (+4); refine until ET local matches midnight / end-of-day.
  let startMs = Date.UTC(y, m - 1, d, 4, 0, 0);
  let endMs = Date.UTC(y, m - 1, d + 1, 3, 59, 59);

  for (let i = 0; i < 6; i++) {
    const startEt = toNflEtDateString(new Date(startMs));
    const endEt = toNflEtDateString(new Date(endMs));
    if (startEt === dateEt && endEt === dateEt) break;
    if (startEt > dateEt) startMs -= 3600_000;
    else if (startEt < dateEt) startMs += 3600_000;
    if (endEt > dateEt) endMs -= 3600_000;
    else if (endEt < dateEt) endMs += 3600_000;
  }

  return { startsAt: new Date(startMs), endsAt: new Date(endMs) };
}

/**
 * True when a scheduled kickoff belongs to the target NFL fixture date in ET
 * (primary) or UTC calendar date (fallback for feed payloads).
 */
export function kickoffMatchesFixtureDate(kickoff: Date, fixtureDate: string): boolean {
  return toNflEtDateString(kickoff) === fixtureDate || kickoff.toISOString().slice(0, 10) === fixtureDate;
}

const NFL_GAME_WINDOW_DAY_OFFSET: Record<Exclude<NFLGameWindow, 'CUSTOM'>, number> = {
  TNF: 2,
  SUNDAY: 5,
  MNF: 6,
};

/**
 * Return the Tuesday that starts the NFL week containing `gameDate`.
 * NFL weeks run Tuesday → Monday regardless of the day's calendar role.
 */
export function getNflWeekStartTuesday(gameDate: string): string {
  const d = new Date(`${gameDate}T12:00:00Z`);
  const daysSinceTuesday = (d.getUTCDay() + 5) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceTuesday);
  return d.toISOString().split('T')[0];
}

/**
 * Map a Daily Reveal day's calendar date + game window to the feed-service
 * date that actually hosts NFL fixtures (TNF Thursday, Sunday slate, MNF Monday).
 */
export function resolveNflFixtureDate(gameDate: string, gameWindow: NFLGameWindow): string {
  if (gameWindow === 'CUSTOM') return gameDate;
  const weekStart = getNflWeekStartTuesday(gameDate);
  const target = new Date(`${weekStart}T12:00:00Z`);
  target.setUTCDate(target.getUTCDate() + NFL_GAME_WINDOW_DAY_OFFSET[gameWindow]);
  return target.toISOString().split('T')[0];
}

/** Look up a day's category config by 1-based day number. */
export function getNflDayConfigByNumber(dayNumber: number): NFLDailyRevealDayConfig | null {
  return NFL_DAILY_REVEAL_CONFIG.days.find(d => d.dayNumber === dayNumber) ?? null;
}

/** Look up a day's category config by category key, e.g. `ground_king`. */
export function getNflDayConfigByCategory(categoryKey: string): NFLDailyRevealDayConfig | null {
  return NFL_DAILY_REVEAL_CONFIG.days.find(d => d.categoryKey === categoryKey) ?? null;
}

/**
 * Grade a prop result against a frozen threshold + operator.
 * Returns `'won'` or `'lost'` — exceptional outcomes (void/credit) are handled
 * by the scoring worker's policy layer, not here.
 */
export function gradeNflProp(
  resultValue: number,
  threshold: number,
  operator: NFLPropOperator
): 'won' | 'lost' {
  switch (operator) {
    case 'over':
      return resultValue > threshold ? 'won' : 'lost';
    case 'over_or_equal':
      return resultValue >= threshold ? 'won' : 'lost';
    case 'over_or_true':
      return resultValue >= 1 ? 'won' : 'lost';
    default:
      return 'lost';
  }
}
